"use client"

import { useTranslation } from "react-i18next"
import {
  Calendar,
  Info,
  DollarSign,
  RefreshCcw,
  Bell,
  FileText,
  Package,
  User,
  Clock,
  BarChart,
  MessageSquare,
  Star,
  CreditCard,
  File, NotebookPen
} from "lucide-react"
import { cn } from "@/lib/utils"
import { WriteToClientButton } from "@/components/inbox/InboxEntry"
import { useAccessPolicy } from "@/hooks/access/useAccess"

interface ModalSidebarProps {
  activeSection: string
  onSectionChange: (section: string) => void
  clientName: string
  /** the card, for Boona Direct's "Write to client" */
  clientId?: number | null
}

export function ModalSidebar({ activeSection, onSectionChange, clientName, clientId }: ModalSidebarProps) {
  const { t } = useTranslation()
  // writing consumables off needs records_consumables_edit_access (the backend
  // ignores a write-off from anyone else) — a "worker" doesn't see the section
  const canWriteOff = useAccessPolicy().records.writeOff

  const sections = [
    { id: "details", icon: <Calendar className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.details", "Appointment details") },
    { id: "status", icon: <Info className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.status", "Visit status") },
    { id: "payment", icon: <DollarSign className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.payment", "Visit payment") },
    // { id: "notifications", icon: <Bell className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.notifications", "Notifications") },
    ...(canWriteOff ? [{ id: "consumables", icon: <Package className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.consumables", "Write-off consumables") }] : []),
    { id: "changes", icon: <FileText className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.changes", "Changes history") },
  ]

  const clientSections = [
    { id: "client-data", icon: <User className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.client_data", "Client data") },
    // the client's notes with their photos and videos (boona docs/codebase/attachments/; owner's pick: own section)
    { id: "client-notes", icon: <NotebookPen className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.client_notes", "Client notes") },
    { id: "visit-history", icon: <Clock className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.visit_history", "Visit history") },
    //{ id: "statistics", icon: <BarChart className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.statistics", "Statistics") },
    // Communication trace (backend messenger README §8) — Call History is
    // gone with the v0 scaffold, so the label is just "Messages".
    { id: "messages", icon: <MessageSquare className="w-4 h-4 mr-2" />, label: t("appointment.sidebar.messages", "Messages") },
  ]

  return (
    // shrink-0: squeezed by the content, the labels wrapped onto two lines and doubled
    // each item. Short screens (≤700 px tall: a 1366×768 laptop at 125%) get half the
    // padding, so all eight items fit without a scrollbar (2026-10-02, salon 1321617).
    <div className="w-[260px] shrink-0 bg-gray-50 border-r flex flex-col h-full">
      {/* Fixed header */}
      {/* 
      <div className="bg-yellow-100 p-4 font-medium text-sm">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2" />
          <span>Детали записи</span>
        </div>
      </div>
      */}
      {/* Scrollable content area that includes both section groups */}
      <div className="flex-1 overflow-y-auto">
        {/* First section group */}
        <div>
          {sections.map((section) => (
            <div
              key={section.id}
              className={cn("p-4 [@media(max-height:700px)]:py-2 border-b cursor-pointer", activeSection === section.id && "bg-yellow-50")}
              onClick={() => onSectionChange(section.id)}
            >
              <div className="flex items-center text-gray-700 text-xs">
                {section.icon}
                <span>{section.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Client info divider */}
        <div className="p-4 [@media(max-height:700px)]:py-2 border-t">
          <div className="text-xs text-gray-500 mb-1">{t("appointment.sidebar.client", "Client:")}</div>
          <div className="font-medium text-sm">{clientName || t("common.no_name", "No name*")}</div>
          <WriteToClientButton clientId={clientId} className="mt-2 w-full" />
          <div className="w-full h-0.5 bg-gray-300 my-2"></div>
        </div>

        {/* Second section group */}
        <div>
          {clientSections.map((section) => (
            <div
              key={section.id}
              className={cn("p-4 [@media(max-height:700px)]:py-2 cursor-pointer", activeSection === section.id && "bg-yellow-50")}
              onClick={() => onSectionChange(section.id)}
            >
              <div className="flex items-center text-gray-700 text-xs">
                {section.icon}
                <span>{section.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
